import { useCallback, useState, type CSSProperties } from 'react';
import Sheet from './Sheet.js';
import { completeTask, KEEPALIVE, snoozeTask, updateTask } from './api.js';
import { addDays, minutesLabel, nextMonday, shortDate, taskCode } from './format.js';
import { useToast } from './toast.js';
import { Check, Dot, DueChip } from './ui.js';
import Swipeable, { type SwipeAction } from '../ui/Swipeable.js';
import type { SnoozePreset, TaskPatch, TaskRow, TaskStatus } from './types.js';

/**
 * Task rows and the actions behind them, shared by Home and Tasks. Every state
 * change goes through `useTaskActions`: the row disappears at once, a toast
 * offers Undo for five seconds, and only then is the request sent.
 */

export const SNOOZE_PRESETS: readonly { until: SnoozePreset; label: string; done: string }[] = [
  { until: '1h', label: '1 hour', done: 'for an hour' },
  { until: 'tonight', label: 'Tonight', done: 'until tonight' },
  { until: 'tomorrow', label: 'Tomorrow', done: 'until tomorrow' },
  { until: 'next_week', label: 'Next week', done: 'until next week' },
];

/** What an action needs to know about a task (the Now card has no full row). */
export type TaskRef = Pick<TaskRow, 'id' | 'shortCode' | 'title'>;

export interface TaskActions {
  /** Ids applied optimistically and not yet confirmed by a reload. */
  hidden: ReadonlySet<string>;
  complete(t: TaskRef): void;
  reopen(t: TaskRef): void;
  snooze(t: TaskRef, until: SnoozePreset): void;
  setStatus(t: TaskRef, status: Extract<TaskStatus, 'next' | 'someday' | 'dropped'>): void;
  /** A To-sort decision with a label for the toast ("Next, due Tomorrow"). */
  sort(t: TaskRef, patch: TaskPatch, label: string): void;
}

const STATUS_DONE: Record<'next' | 'someday' | 'dropped', string> = {
  next: 'moved to Next',
  someday: 'moved to Someday',
  dropped: 'dropped',
};

export function useTaskActions(reload: () => Promise<void>): TaskActions {
  const toast = useToast();
  const [hidden, setHidden] = useState<ReadonlySet<string>>(new Set());

  const change = useCallback(
    (t: TaskRef, text: string, commit: () => Promise<unknown>) => {
      const unhide = (): void =>
        setHidden((s) => {
          const n = new Set(s);
          n.delete(t.id);
          return n;
        });
      setHidden((s) => new Set(s).add(t.id));
      toast.defer({
        text,
        commit,
        onUndo: unhide,
        // Keep the row hidden until the fresh list has replaced it: no flash back.
        onDone: () => void reload().finally(unhide),
        onError: unhide,
        failText: `Couldn't update ${taskCode(t.shortCode)}`,
      });
    },
    [toast, reload],
  );

  const code = (t: TaskRef): string => taskCode(t.shortCode);

  return {
    hidden,
    complete: (t) => change(t, `Done ${code(t)} ${t.title}`, () => completeTask(t.id, KEEPALIVE)),
    reopen: (t) => change(t, `Reopened ${code(t)}`, () => updateTask(t.id, { status: 'next' }, KEEPALIVE)),
    snooze: (t, until) => {
      const p = SNOOZE_PRESETS.find((s) => s.until === until);
      change(t, `Snoozed ${code(t)} ${p?.done ?? ''}`.trim(), () => snoozeTask(t.id, until, KEEPALIVE));
    },
    setStatus: (t, status) =>
      change(t, `${code(t)} ${STATUS_DONE[status]}`, () => updateTask(t.id, { status }, KEEPALIVE)),
    sort: (t, patch, label) => change(t, `${code(t)} → ${label}`, () => updateTask(t.id, patch, KEEPALIVE)),
  };
}

// --- Row -----------------------------------------------------------------------------------

/**
 * One task: round check (the one primary action), title, a quiet meta line
 * (due word — red only when late —, sphere dot, estimate, waiting). Touch:
 * swipe right completes, swipe left shows Tomorrow / More, long-press opens
 * the menu. Pointer: the ⋯ button opens the same menu.
 */
export function TaskItem({
  task,
  today,
  color,
  done = false,
  onToggle,
  onMenu,
  onSnooze,
  action,
}: {
  task: TaskRow;
  today: string;
  /** The task's sphere colour. */
  color: string;
  done?: boolean;
  onToggle: () => void;
  onMenu?: () => void;
  /** Swipe-left "Tomorrow" (snooze until tomorrow). */
  onSnooze?: () => void;
  /** An extra row button (e.g. "Sort" in To sort). */
  action?: { label: string; onClick: () => void };
}): JSX.Element {
  const code = taskCode(task.shortCode);
  const est = minutesLabel(task.estimateMin);
  // A waiting task's start date is its chase date; once it has come, say so.
  const chaseDue = task.status === 'waiting' && task.startOn !== null && task.startOn <= today;
  const chase = task.status === 'waiting' && task.startOn && !chaseDue ? shortDate(task.startOn, today) : null;
  const trailing: SwipeAction[] = [];
  if (onSnooze && !done) trailing.push({ label: 'Tomorrow', icon: 'moon', tone: 'warn', onAction: onSnooze });
  if (onMenu) trailing.push({ label: 'More', icon: 'more', tone: 'neutral', onAction: onMenu });
  return (
    <Swipeable
      className={`lo-task${done ? ' lo-task--done' : ''}`}
      style={{ '--lo-sphere': color } as CSSProperties}
      leading={{ label: done ? 'Reopen' : 'Done', icon: done ? 'undo' : 'check', tone: 'good', onAction: onToggle }}
      trailing={trailing}
      onLongPress={onMenu}
    >
      <div className="lo-task__row">
        <Check checked={done} onChange={onToggle} label={done ? `Reopen ${code}` : `Complete ${code} ${task.title}`} />
        <div className="lo-task__main">
          <span className="lo-task__title">{task.title}</span>
          <span className="lo-task__meta">
            {!done && <DueChip dueOn={task.dueOn} today={today} />}
            {task.status === 'waiting' && task.waitingOn && (
              <span className="lo-task__waiting">
                waiting on {task.waitingOn}
                {chase && ` · chase ${chase}`}
              </span>
            )}
            {chaseDue && <span className="lo-due lo-due--today">chase now</span>}
            {est && <span className="lo-task__est">{est}</span>}
            {task.repeat && <span className="lo-task__est">↻ {task.repeat}</span>}
            <span className="lo-task__sphere">
              <Dot color={color} />
              <span className="lo-code">{code}</span>
            </span>
          </span>
        </div>
        {action && (
          <button type="button" className="ui-btn ui-btn--secondary ui-btn--sm" onClick={action.onClick}>
            {action.label}
          </button>
        )}
        {onMenu && (
          <button type="button" className="lo-iconbtn lo-task__menu" aria-label={`More for ${code}`} onClick={onMenu}>
            <svg viewBox="0 0 16 16" aria-hidden="true">
              <circle cx="3" cy="8" r="1.4" />
              <circle cx="8" cy="8" r="1.4" />
              <circle cx="13" cy="8" r="1.4" />
            </svg>
          </button>
        )}
      </div>
    </Swipeable>
  );
}

// --- Overflow menu ----------------------------------------------------------------------------

export function TaskActionsSheet({
  task,
  actions,
  onEdit,
  onClose,
}: {
  task: TaskRow;
  actions: TaskActions;
  onEdit?: () => void;
  onClose: () => void;
}): JSX.Element {
  const run = (f: () => void) => () => {
    onClose();
    f();
  };
  return (
    <Sheet title={`${taskCode(task.shortCode)} ${task.title}`} onClose={onClose}>
      <div className="lo-sheet__label">Snooze</div>
      <div className="lo-sheet__grid">
        {SNOOZE_PRESETS.map((p) => (
          <button key={p.until} type="button" className="lo-sheet__opt" onClick={run(() => actions.snooze(task, p.until))}>
            {p.label}
          </button>
        ))}
      </div>
      <div className="lo-sheet__list">
        <button type="button" className="lo-sheet__row" onClick={run(() => actions.setStatus(task, 'someday'))}>
          Someday
        </button>
        <button type="button" className="lo-sheet__row lo-sheet__row--bad" onClick={run(() => actions.setStatus(task, 'dropped'))}>
          Drop
        </button>
        {onEdit && (
          <button type="button" className="lo-sheet__row" onClick={run(onEdit)}>
            Edit details
          </button>
        )}
      </div>
    </Sheet>
  );
}

// --- To-sort sheet (spec §6: Next / Someday / Drop + a date chip) -------------------------------

export function SortSheet({
  task,
  today,
  actions,
  onClose,
}: {
  task: TaskRow;
  today: string;
  actions: TaskActions;
  onClose: () => void;
}): JSX.Element {
  const [date, setDate] = useState('');
  const sort = (patch: TaskPatch, label: string) => () => {
    onClose();
    actions.sort(task, patch, label);
  };
  const dates: [string, string][] = [
    ['Today', today],
    ['Tomorrow', addDays(today, 1)],
    ['Next week', nextMonday(today)],
  ];
  return (
    <Sheet title={`Sort ${taskCode(task.shortCode)} ${task.title}`} onClose={onClose}>
      <div className="lo-sort">
        <button type="button" className="btn" onClick={sort({ status: 'next' }, 'Next')}>
          Next
        </button>
        <button type="button" className="btn btn--ghost" onClick={sort({ status: 'someday', dueOn: null }, 'Someday')}>
          Someday
        </button>
        <button type="button" className="btn btn--ghost lo-btn--bad" onClick={sort({ status: 'dropped' }, 'Dropped')}>
          Drop
        </button>
      </div>
      <div className="lo-sheet__label">Next, due</div>
      <div className="lo-chips">
        {dates.map(([label, ymd]) => (
          <button key={label} type="button" className="lo-chip" onClick={sort({ status: 'next', dueOn: ymd }, `Next, due ${label.toLowerCase()}`)}>
            {label}
          </button>
        ))}
        <input
          type="date"
          className="lo-chip lo-chip--date"
          aria-label="Pick a due date"
          min={today}
          value={date}
          onChange={(e) => setDate(e.target.value)}
        />
        {date && (
          <button type="button" className="lo-chip lo-chip--on" onClick={sort({ status: 'next', dueOn: date }, `Next, due ${shortDate(date, today)}`)}>
            Set {shortDate(date, today)}
          </button>
        )}
      </div>
    </Sheet>
  );
}
